// ============================================================================
// NoTomorrow3D - Internationalization (i18n) System
// ============================================================================

export type Language = 'en' | 'zh' | 'ko' | 'ja' | 'es';

export interface Translations {
  // Navigation
  nav: {
    home: string;
    play: string;
    stats: string;
    referrals: string;
    help: string;
  };
  
  // Game state
  game: {
    round: string;
    timeLeft: string;
    pot: string;
    keys: string;
    yourKeys: string;
    lastBuyer: string;
    price: string;
    buyKeys: string;
    connectWallet: string;
    claimDividends: string;
    pendingDividends: string;
  };
  
  // Teams
  teams: {
    chooseTeam: string;
    whale: string;
    bull: string;
    bear: string;
    snake: string;
    shark: string;
    dolphin: string;
    tiger: string;
    eagle: string;
  };
  
  // Events
  events: {
    bloodMoon: string;
    assassinMode: string;
    goldenRound: string;
    dividendStorm: string;
    lotteryRound: string;
    extinctionEvent: string;
    ghostKey: string;
  };
  
  // Referral dashboard
  referrals: {
    title: string;
    yourLink: string;
    copyLink: string;
    copied: string;
    totalEarnings: string;
    totalReferrals: string;
    thisRound: string;
    allTime: string;
    recentReferrals: string;
    noReferrals: string;
    shareNow: string;
  };
  
  // Stats
  stats: {
    leaderboard: string;
    topPlayers: string;
    topReferrers: string;
    teamLeaderboard: string;
    yourStats: string;
    totalWinnings: string;
    roundsWon: string;
    keysBought: string;
  };
  
  // Actions
  actions: {
    buy: string;
    claim: string;
    share: string;
    copy: string;
    close: string;
    confirm: string;
    cancel: string;
    loading: string;
  };
  
  // Alerts
  alerts: {
    roundEnding: string;
    roundEnded: string;
    bigPurchase: string;
    chaosEvent: string;
    youWon: string;
    dividendsReady: string;
  };
  
  // Errors
  errors: {
    walletNotConnected: string;
    insufficientBalance: string;
    transactionFailed: string;
    networkError: string;
  };
}

// ============================================================================
// ENGLISH (Default)
// ============================================================================

const en: Translations = {
  nav: {
    home: 'Home',
    play: 'Play',
    stats: 'Stats',
    referrals: 'Referrals',
    help: 'Help',
  },
  game: {
    round: 'Round',
    timeLeft: 'Time Left',
    pot: 'Pot',
    keys: 'Keys',
    yourKeys: 'Your Keys',
    lastBuyer: 'Last Buyer',
    price: 'Key Price',
    buyKeys: 'Buy Keys',
    connectWallet: 'Connect Wallet',
    claimDividends: 'Claim Dividends',
    pendingDividends: 'Pending Dividends',
  },
  teams: {
    chooseTeam: 'Choose Your Team',
    whale: 'Whale',
    bull: 'Bull',
    bear: 'Bear',
    snake: 'Viper',
    shark: 'Shark',
    dolphin: 'Dolphin',
    tiger: 'Tiger',
    eagle: 'Eagle',
  },
  events: {
    bloodMoon: '🌑 Blood Moon Active!',
    assassinMode: '🗡️ Assassin Mode!',
    goldenRound: '✨ Golden Round!',
    dividendStorm: '🌪️ Dividend Storm!',
    lotteryRound: '🎰 Lottery Round!',
    extinctionEvent: '☄️ Extinction Event!',
    ghostKey: '👻 Ghost Key!',
  },
  referrals: {
    title: 'Referral Dashboard',
    yourLink: 'Your Referral Link',
    copyLink: 'Copy Link',
    copied: 'Copied!',
    totalEarnings: 'Total collected',
    totalReferrals: 'Total Referrals',
    thisRound: 'This Round',
    allTime: 'All Time',
    recentReferrals: 'Recent Referrals',
    noReferrals: 'No referrals yet. Share your link to get 5% of the test FRAME your recruits spend.',
    shareNow: 'Share Now',
  },
  stats: {
    leaderboard: 'Leaderboard',
    topPlayers: 'Top Players',
    topReferrers: 'Top Referrers',
    teamLeaderboard: 'Team Leaderboard',
    yourStats: 'Your Stats',
    totalWinnings: 'Total Winnings',
    roundsWon: 'Rounds Won',
    keysBought: 'Keys Bought',
  },
  actions: {
    buy: 'Buy',
    claim: 'Claim',
    share: 'Share',
    copy: 'Copy',
    close: 'Close',
    confirm: 'Confirm',
    cancel: 'Cancel',
    loading: 'Loading...',
  },
  alerts: {
    roundEnding: '⏰ Round ending soon!',
    roundEnded: '🎉 Round ended!',
    bigPurchase: '🐋 Big purchase alert!',
    chaosEvent: '🌀 Chaos event triggered!',
    youWon: '🏆 You won!',
    dividendsReady: '💰 Dividends ready to claim!',
  },
  errors: {
    walletNotConnected: 'Please connect your wallet',
    insufficientBalance: 'Insufficient balance',
    transactionFailed: 'Transaction failed',
    networkError: 'Network error. Please try again.',
  },
};

// ============================================================================
// CHINESE (Simplified)
// ============================================================================

const zh: Translations = {
  nav: {
    home: '首页',
    play: '游戏',
    stats: '统计',
    referrals: '推荐',
    help: '帮助',
  },
  game: {
    round: '回合',
    timeLeft: '剩余时间',
    pot: '奖池',
    keys: '钥匙',
    yourKeys: '你的钥匙',
    lastBuyer: '最后买家',
    price: '钥匙价格',
    buyKeys: '购买钥匙',
    connectWallet: '连接钱包',
    claimDividends: '领取分红',
    pendingDividends: '待领分红',
  },
  teams: {
    chooseTeam: '选择你的队伍',
    whale: '鲸鱼',
    bull: '公牛',
    bear: '熊',
    snake: '毒蛇',
    shark: '鲨鱼',
    dolphin: '海豚',
    tiger: '老虎',
    eagle: '鹰',
  },
  events: {
    bloodMoon: '🌑 血月降临!',
    assassinMode: '🗡️ 刺客模式!',
    goldenRound: '✨ 黄金回合!',
    dividendStorm: '🌪️ 分红风暴!',
    lotteryRound: '🎰 彩票回合!',
    extinctionEvent: '☄️ 灭绝事件!',
    ghostKey: '👻 幽灵钥匙!',
  },
  referrals: {
    title: '推荐仪表板',
    yourLink: '你的推荐链接',
    copyLink: '复制链接',
    copied: '已复制!',
    totalEarnings: '累计领取',
    totalReferrals: '总推荐数',
    thisRound: '本回合',
    allTime: '历史总计',
    recentReferrals: '最近推荐',
    noReferrals: '还没有推荐。分享你的链接，每笔购买赚取5%!',
    shareNow: '立即分享',
  },
  stats: {
    leaderboard: '排行榜',
    topPlayers: '顶级玩家',
    topReferrers: '顶级推荐人',
    teamLeaderboard: '队伍排行榜',
    yourStats: '你的统计',
    totalWinnings: '总赢利',
    roundsWon: '获胜回合',
    keysBought: '购买钥匙数',
  },
  actions: {
    buy: '购买',
    claim: '领取',
    share: '分享',
    copy: '复制',
    close: '关闭',
    confirm: '确认',
    cancel: '取消',
    loading: '加载中...',
  },
  alerts: {
    roundEnding: '⏰ 回合即将结束!',
    roundEnded: '🎉 回合结束!',
    bigPurchase: '🐋 大额购买警报!',
    chaosEvent: '🌀 混乱事件触发!',
    youWon: '🏆 你赢了!',
    dividendsReady: '💰 分红可领取!',
  },
  errors: {
    walletNotConnected: '请连接你的钱包',
    insufficientBalance: '余额不足',
    transactionFailed: '交易失败',
    networkError: '网络错误，请重试',
  },
};

// ============================================================================
// KOREAN
// ============================================================================

const ko: Translations = {
  nav: {
    home: '홈',
    play: '플레이',
    stats: '통계',
    referrals: '추천',
    help: '도움말',
  },
  game: {
    round: '라운드',
    timeLeft: '남은 시간',
    pot: '상금',
    keys: '키',
    yourKeys: '내 키',
    lastBuyer: '마지막 구매자',
    price: '키 가격',
    buyKeys: '키 구매',
    connectWallet: '지갑 연결',
    claimDividends: '배당금 청구',
    pendingDividends: '대기 중인 배당금',
  },
  teams: {
    chooseTeam: '팀을 선택하세요',
    whale: '고래',
    bull: '황소',
    bear: '곰',
    snake: '바이퍼',
    shark: '상어',
    dolphin: '돌고래',
    tiger: '호랑이',
    eagle: '독수리',
  },
  events: {
    bloodMoon: '🌑 블러드 문 활성화!',
    assassinMode: '🗡️ 암살자 모드!',
    goldenRound: '✨ 골든 라운드!',
    dividendStorm: '🌪️ 배당 폭풍!',
    lotteryRound: '🎰 복권 라운드!',
    extinctionEvent: '☄️ 멸종 이벤트!',
    ghostKey: '👻 유령 키!',
  },
  referrals: {
    title: '추천 대시보드',
    yourLink: '내 추천 링크',
    copyLink: '링크 복사',
    copied: '복사됨!',
    totalEarnings: '총 수령액',
    totalReferrals: '총 추천 수',
    thisRound: '이번 라운드',
    allTime: '전체 기간',
    recentReferrals: '최근 추천',
    noReferrals: '아직 추천이 없습니다. 링크를 공유하고 모든 구매에서 5%를 받으세요!',
    shareNow: '지금 공유',
  },
  stats: {
    leaderboard: '리더보드',
    topPlayers: '상위 플레이어',
    topReferrers: '상위 추천인',
    teamLeaderboard: '팀 리더보드',
    yourStats: '내 통계',
    totalWinnings: '총 상금',
    roundsWon: '이긴 라운드',
    keysBought: '구매한 키',
  },
  actions: {
    buy: '구매',
    claim: '청구',
    share: '공유',
    copy: '복사',
    close: '닫기',
    confirm: '확인',
    cancel: '취소',
    loading: '로딩 중...',
  },
  alerts: {
    roundEnding: '⏰ 라운드가 곧 끝납니다!',
    roundEnded: '🎉 라운드 종료!',
    bigPurchase: '🐋 대량 구매 알림!',
    chaosEvent: '🌀 카오스 이벤트 발동!',
    youWon: '🏆 승리했습니다!',
    dividendsReady: '💰 배당금 청구 가능!',
  },
  errors: {
    walletNotConnected: '지갑을 연결하세요',
    insufficientBalance: '잔액 부족',
    transactionFailed: '거래 실패',
    networkError: '네트워크 오류. 다시 시도하세요.',
  },
};

// ============================================================================
// JAPANESE
// ============================================================================

const ja: Translations = {
  nav: {
    home: 'ホーム',
    play: 'プレイ',
    stats: '統計',
    referrals: '紹介',
    help: 'ヘルプ',
  },
  game: {
    round: 'ラウンド',
    timeLeft: '残り時間',
    pot: 'ポット',
    keys: 'キー',
    yourKeys: 'あなたのキー',
    lastBuyer: '最後の購入者',
    price: 'キー価格',
    buyKeys: 'キーを購入',
    connectWallet: 'ウォレットを接続',
    claimDividends: '配当を請求',
    pendingDividends: '保留中の配当',
  },
  teams: {
    chooseTeam: 'チームを選択',
    whale: 'クジラ',
    bull: 'ブル',
    bear: 'ベア',
    snake: 'バイパー',
    shark: 'シャーク',
    dolphin: 'イルカ',
    tiger: 'タイガー',
    eagle: 'イーグル',
  },
  events: {
    bloodMoon: '🌑 ブラッドムーン発動!',
    assassinMode: '🗡️ アサシンモード!',
    goldenRound: '✨ ゴールデンラウンド!',
    dividendStorm: '🌪️ 配当ストーム!',
    lotteryRound: '🎰 ロッタリーラウンド!',
    extinctionEvent: '☄️ 絶滅イベント!',
    ghostKey: '👻 ゴーストキー!',
  },
  referrals: {
    title: '紹介ダッシュボード',
    yourLink: 'あなたの紹介リンク',
    copyLink: 'リンクをコピー',
    copied: 'コピーしました!',
    totalEarnings: '受取合計',
    totalReferrals: '総紹介数',
    thisRound: '今ラウンド',
    allTime: '累計',
    recentReferrals: '最近の紹介',
    noReferrals: 'まだ紹介がありません。リンクを共有して全購入の5%を獲得!',
    shareNow: '今すぐ共有',
  },
  stats: {
    leaderboard: 'リーダーボード',
    topPlayers: 'トッププレイヤー',
    topReferrers: 'トップ紹介者',
    teamLeaderboard: 'チームリーダーボード',
    yourStats: 'あなたの統計',
    totalWinnings: '総獲得額',
    roundsWon: '勝利ラウンド',
    keysBought: '購入キー数',
  },
  actions: {
    buy: '購入',
    claim: '請求',
    share: '共有',
    copy: 'コピー',
    close: '閉じる',
    confirm: '確認',
    cancel: 'キャンセル',
    loading: '読み込み中...',
  },
  alerts: {
    roundEnding: '⏰ ラウンドがまもなく終了!',
    roundEnded: '🎉 ラウンド終了!',
    bigPurchase: '🐋 大口購入アラート!',
    chaosEvent: '🌀 カオスイベント発動!',
    youWon: '🏆 勝利しました!',
    dividendsReady: '💰 配当請求可能!',
  },
  errors: {
    walletNotConnected: 'ウォレットを接続してください',
    insufficientBalance: '残高不足',
    transactionFailed: '取引失敗',
    networkError: 'ネットワークエラー。再試行してください。',
  },
};

// ============================================================================
// SPANISH
// ============================================================================

const es: Translations = {
  nav: {
    home: 'Inicio',
    play: 'Jugar',
    stats: 'Estadísticas',
    referrals: 'Referidos',
    help: 'Ayuda',
  },
  game: {
    round: 'Ronda',
    timeLeft: 'Tiempo Restante',
    pot: 'Bote',
    keys: 'Llaves',
    yourKeys: 'Tus Llaves',
    lastBuyer: 'Último Comprador',
    price: 'Precio de Llave',
    buyKeys: 'Comprar Llaves',
    connectWallet: 'Conectar Cartera',
    claimDividends: 'Reclamar Dividendos',
    pendingDividends: 'Dividendos Pendientes',
  },
  teams: {
    chooseTeam: 'Elige Tu Equipo',
    whale: 'Ballena',
    bull: 'Toro',
    bear: 'Oso',
    snake: 'Víbora',
    shark: 'Tiburón',
    dolphin: 'Delfín',
    tiger: 'Tigre',
    eagle: 'Águila',
  },
  events: {
    bloodMoon: '🌑 ¡Luna de Sangre Activa!',
    assassinMode: '🗡️ ¡Modo Asesino!',
    goldenRound: '✨ ¡Ronda Dorada!',
    dividendStorm: '🌪️ ¡Tormenta de Dividendos!',
    lotteryRound: '🎰 ¡Ronda de Lotería!',
    extinctionEvent: '☄️ ¡Evento de Extinción!',
    ghostKey: '👻 ¡Llave Fantasma!',
  },
  referrals: {
    title: 'Panel de Referidos',
    yourLink: 'Tu Enlace de Referido',
    copyLink: 'Copiar Enlace',
    copied: '¡Copiado!',
    totalEarnings: 'Total cobrado',
    totalReferrals: 'Total de Referidos',
    thisRound: 'Esta Ronda',
    allTime: 'Todo el Tiempo',
    recentReferrals: 'Referidos Recientes',
    noReferrals: 'Sin referidos aún. ¡Comparte tu enlace y gana 5% en cada compra!',
    shareNow: 'Compartir Ahora',
  },
  stats: {
    leaderboard: 'Tabla de Líderes',
    topPlayers: 'Mejores Jugadores',
    topReferrers: 'Mejores Referidores',
    teamLeaderboard: 'Tabla de Equipos',
    yourStats: 'Tus Estadísticas',
    totalWinnings: 'Ganancias Totales',
    roundsWon: 'Rondas Ganadas',
    keysBought: 'Llaves Compradas',
  },
  actions: {
    buy: 'Comprar',
    claim: 'Reclamar',
    share: 'Compartir',
    copy: 'Copiar',
    close: 'Cerrar',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    loading: 'Cargando...',
  },
  alerts: {
    roundEnding: '⏰ ¡La ronda está por terminar!',
    roundEnded: '🎉 ¡Ronda terminada!',
    bigPurchase: '🐋 ¡Alerta de compra grande!',
    chaosEvent: '🌀 ¡Evento caótico activado!',
    youWon: '🏆 ¡Ganaste!',
    dividendsReady: '💰 ¡Dividendos listos para reclamar!',
  },
  errors: {
    walletNotConnected: 'Por favor conecta tu cartera',
    insufficientBalance: 'Saldo insuficiente',
    transactionFailed: 'Transacción fallida',
    networkError: 'Error de red. Intenta de nuevo.',
  },
};

// ============================================================================
// EXPORTS
// ============================================================================

export const translations: Record<Language, Translations> = {
  en,
  zh,
  ko,
  ja,
  es,
};

export const languageNames: Record<Language, string> = {
  en: 'English',
  zh: '中文',
  ko: '한국어',
  ja: '日本語',
  es: 'Español',
};

export const defaultLanguage: Language = 'en';

export function getTranslations(lang: Language): Translations {
  return translations[lang] || translations[defaultLanguage];
}

export function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return defaultLanguage;
  
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  
  if (browserLang in translations) {
    return browserLang as Language;
  }
  
  return defaultLanguage;
}
